import axios, { AxiosInstance, AxiosRequestHeaders } from 'axios';
import { CustomAxiosRequestConfig } from '../types';
import localForage from 'localforage';
import { performLogout } from '@/services/logoutService';
import { TokenService } from '@/services/tokenService';
import { getEnv } from '@/utils/env';
import { decodeToken } from '@/utils/tokenUtils';
import i18n from '@/i18n';

const baseUrl = getEnv('VITE_API_BASEPATH', '');

function resolveAcceptLanguage(): string {
    const lng = i18n?.resolvedLanguage || localStorage.getItem('lng') || navigator.language || 'en';

    // Example normalization: zh-Hant (app) -> zh-TW (backend)
    if (lng === 'zh-Hant' || lng.startsWith('zh-Hant')) return 'zh-TW';
    return lng;
}

const httpService: AxiosInstance = axios.create({
    baseURL: baseUrl,
    timeout: 60000,
    headers: { 'Content-Type': 'application/json' },
});

i18n?.on?.('languageChanged', (lng) => {
    const mapped = lng === 'zh-Hant' ? 'zh-TW' : lng;
    httpService.defaults.headers.common['Accept-Language'] = mapped;
});

const isTokenExpired = async (): Promise<boolean> => {
    const tokenExpires = localStorage.getItem('tokenExpires');
    const hasAnyToken =
        !!(await localForage.getItem('login_token')) || !!(await localForage.getItem('corporate_token'));

    if (!hasAnyToken) return false;
    if (!tokenExpires) return false;

    const expirationTime = parseInt(tokenExpires, 10);
    const currentTime = Math.floor(Date.now() / 1000);
    return expirationTime <= currentTime;
};

async function logoutHandler(message?: string) {
    if (message) {
        localStorage.setItem('autoLogoutMessage', message);
    }
    const raw = await localForage.getItem<string>('corporate_token');
    const decoded = raw ? decodeToken(raw) : undefined;
    await performLogout(decoded ? [decoded] : []);
    window.location.href = '/login';
}

httpService.interceptors.request.use(
    async (config: CustomAxiosRequestConfig) => {
        try {
            config.headers = { ...config.headers } as AxiosRequestHeaders;

            (config.headers as Record<string, string>)['Accept-Language'] = resolveAcceptLanguage();

            const tokenType = config.tokenType || 'corporate_token';
            let tokenResponse: string | null =
                tokenType === 'login_token'
                    ? await localForage.getItem('login_token')
                    : await localForage.getItem('corporate_token');

            if (tokenType === 'login_token') {
                const hadLoginSession = await localForage.getItem<boolean>('hasLoginSession');
                tokenResponse = await localForage.getItem('login_token');

                if (hadLoginSession && !tokenResponse) {
                    await logoutHandler('Missing login session. Please log in again.');
                    return Promise.reject(new Error('Missing login session'));
                }
            } else {
                const hadCorpSession = await localForage.getItem<boolean>('hasCorporateSession');
                tokenResponse = await localForage.getItem('corporate_token');

                if (hadCorpSession && !tokenResponse) {
                    await logoutHandler('Missing corporate session. Please log in again.');
                    return Promise.reject(new Error('Missing corporate session'));
                }
            }

            if (await isTokenExpired()) {
                await TokenService.refreshToken();
                if (tokenType === 'login_token') {
                    tokenResponse = await localForage.getItem('login_token');
                } else {
                    tokenResponse = await localForage.getItem('corporate_token');
                }
            }

            if (tokenResponse) {
                const decodedToken = decodeToken(tokenResponse);
                (config.headers as Record<string, string>)['Authorization'] = `Bearer ${decodedToken}`;
            }

            if (config.url?.includes('/login') || config.url?.includes('/corporate/login')) {
                return config;
            }
        } catch (error) {
            console.error('Error in request interceptor:', error);
        }

        return config;
    },
    (error) => Promise.reject(error),
);

httpService.interceptors.response.use(
    async (response) => {
        const apiStatus = response.data.status;
        const apiMsg = response.data.message || 'API error';
        const original = response.config as CustomAxiosRequestConfig;

        if (apiStatus === 401) {
            const errorCode = response.data?.data?.error_code || null;
            if (errorCode === '0x02FF03') {
                try {
                    original._retryCount = (original._retryCount || 0) + 1;
                    if (original._retryCount > 5) {
                        await logoutHandler('Session expired. Please log in again.');
                        return Promise.reject(new Error(apiMsg));
                    }
                    const newToken = await TokenService.refreshToken();
                    if (newToken) {
                        original.headers = original.headers ?? {};
                        (original.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
                        return httpService(original);
                    }
                } catch {
                    /* fall through to logout below */
                }
                await logoutHandler('Session expired. Please log in again.');
                return Promise.reject(new Error(apiMsg));
            }
            return {
                ...response,
                data: { ...response.data, isSuccess: false },
            };
        }

        if (apiStatus === 400) {
            const resp = response.data?.data?.response;
            if (resp === 'expired_password' || resp === 'invalid_token_provided1') {
                await logoutHandler(apiMsg);
                return Promise.reject(new Error(apiMsg));
            }
            if (resp === 'current_token_valid') {
                const retryConfig = response.config as CustomAxiosRequestConfig;
                retryConfig._retryCount = (retryConfig._retryCount || 0) + 1;

                if (retryConfig._retryCount <= 5) {
                    return httpService(retryConfig);
                } else {
                    await logoutHandler('Session is invalid or expired. Please log in again.');
                    return Promise.reject(new Error(apiMsg));
                }
            }
            // Do not return promise reject!
            return {
                ...response,
                data: { ...response.data, isSuccess: false },
            };
        }
        if (apiStatus !== 200) {
            // Do not return promise reject!
            return {
                ...response,
                data: { ...response.data, isSuccess: false },
            };
        }

        return {
            ...response,
            data: { ...response.data, isSuccess: true },
        };
    },

    async (error) => {
        const originalRequest = error.config as CustomAxiosRequestConfig;
        if (error.response?.status === 401 || error.response?.status === 400) {
            originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

            if (originalRequest._retryCount > 5) {
                await logoutHandler('Session expired or invalid. Please log in again.');
                return Promise.reject(error);
            }

            try {
                const newToken = await TokenService.refreshToken();
                if (newToken) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return httpService(originalRequest);
                }
            } catch {
                /* fall through */
            }
            await logoutHandler('Session expired. Please log in again.');
            return Promise.reject(error);
        }
        if (error.response?.status === 400) {
            const resp = error.response.data?.data?.response;
            const msg = error.response.data?.message || 'Session error';

            if (resp === 'expired_password' || resp === 'invalid_token_provided1') {
                await logoutHandler(msg);
                return Promise.reject(new Error(msg));
            }

            if (resp === 'current_token_valid') {
                return Promise.reject(error);
            }

            await logoutHandler('You have been logged out.');
            return Promise.reject(error);
        }
        return Promise.reject(error);
    },
);

export default httpService;
